'use client';

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Trash2,
  X,
  MailCheck,
  User,
  Clock,
  Briefcase,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

import { DIAS_SEMANA, DiaSemana } from '@/components/constants/dias';

import {
  useInvitarTrabajador,
  editarTrabajador,
} from '@/components/hooks/useInvitarTrabajador';
import {
  useServiciosAsignablesTrabajador,
  ServicioAsignable,
} from '@/components/hooks/useServiciosAsignablesTrabajador';
import {
  useHorariosTrabajadorTodos,
  useHorariosEmpresaTodos,
  CLAVE_BASE,
} from '@/components/hooks/useHorariosPorTemporada';
import {
  useTemporadas,
  formatearFechaCorta,
} from '@/components/hooks/useTemporadas';
import UnsavedChangesDialog from '@/components/pages/dialogs/UnsavedChangesDialog';
import { TemporadaDialog } from '@/components/pages/dialogs/TemporadaDialog';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

type Schedule = {
  id_horario?: string;
  dia_semana: DiaSemana;
  hora_inicio: string;
  hora_fin: string;
};

type WorkerDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  editingWorker: any;
  onSaved: () => void;
};

type Tab = 'datos' | 'horarios' | 'servicios';

const SHEET_CLASS = `
  z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
  [&>button:last-child]:hidden
  flex flex-col
  !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
  w-full max-w-none max-h-[94vh] rounded-t-2xl rounded-b-none
  data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
  sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
  sm:w-[95%] sm:max-w-2xl sm:mx-auto sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl
`;

// Firmas normalizadas para detectar cambios sin guardar. Comparar el JSON
// crudo daba falsos positivos: activo puede llegar como null y volver como
// false, y las franjas cargadas traen id_horario que las nuevas no tienen.
const firmaForm = (f: any) =>
  [
    String(f.email ?? ''),
    String(f.fullName ?? ''),
    String(f.intervaloCita ?? ''),
    String(!!f.activo),
    String(!!f.autocompletarCitas),
    String(f.autocompletarHoras ?? ''),
    String(f.autocompletarDesde ?? ''),
    String(f.autocompletarTipoPago ?? ''),
  ].join('|');

const firmaServicios = (lista: any[]) =>
  [...(lista ?? [])]
    .map((s) => `${String(s.id_servicio)}:${!!s.activo}`)
    .sort()
    .join('|');

const hhmm = (h?: string) => String(h ?? '').slice(0, 5);

const firmaHorarios = (porDia: Record<string, any[]>) =>
  DIAS_SEMANA.map((d) => {
    const franjas = (porDia?.[d] ?? [])
      .map((f: any) => `${hhmm(f.hora_inicio)}-${hhmm(f.hora_fin)}`)
      .sort()
      .join(',');
    return `${d}[${franjas}]`;
  }).join('|');

export const WorkerDialog = ({
  open,
  setOpen,
  editingWorker,
  onSaved,
}: WorkerDialogProps) => {
  const empresaId = localStorage.getItem('id_empresa');
  const idUsuario = localStorage.getItem('id_usuario');

  const [activeTab, setActiveTab] = useState<Tab>('datos');

  const [form, setForm] = useState({
    email: '',
    fullName: '',
    intervaloCita: 30,
    activo: true,
    autocompletarCitas: false,
    autocompletarHoras: 2,
    autocompletarDesde: null as string | null,
    autocompletarTipoPago: 'otros',
  });

  const [schedulesByDay, setSchedulesByDay] = useState<
    Record<DiaSemana, Schedule[]>
  >(() => {
    const inicial = {} as Record<DiaSemana, Schedule[]>;
    DIAS_SEMANA.forEach((d) => (inicial[d] = []));
    return inicial;
  });

  // Guarda la temporada cuyo horario esta volcado en el formulario.
  // false = todavia ninguna. Comparar contra idTemporada evita cargar
  // los datos de la temporada anterior mientras llegan los nuevos.
  const horarioCargadoPara = useRef<string | null | false>(false);
  const [idTemporada, setIdTemporada] = useState<string | null>(null);
  const temporadaInicializada = useRef(false);

  const [newScheduleDay, setNewScheduleDay] = useState<DiaSemana | null>(null);
  const [newScheduleForm, setNewScheduleForm] = useState<Schedule>({
    dia_semana: 'lunes',
    hora_inicio: '',
    hora_fin: '',
  });

  const [localServices, setLocalServices] = useState<any[]>([]);

  // Foto del formulario tal y como se cargo. Comparando contra ella se sabe
  // si hay cambios sin guardar. Se renueva en cada carga (incluido al
  // cambiar de temporada, que recarga las franjas desde base de datos).
  const snapshot = useRef({ form: '', horarios: '', servicios: '' });
  const snapshotTomado = useRef(false);
  const [avisoOpen, setAvisoOpen] = useState(false);
  // Se ha pulsado "Quitarle esta jornada": el fondo pasa a ser su horario
  // general, que es con el que se quedara al guardar.
  const [jornadaQuitada, setJornadaQuitada] = useState(false);
  const accionPendiente = useRef<(() => void) | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showInviteInfo, setShowInviteInfo] = useState(false);
  const [, setSaving] = useState(false);

  const { invitar, loading: loadingInvitar } = useInvitarTrabajador();

  const {
    servicios,
    loading: loadingServicios,
    guardarServiciosTrabajador,
  } = useServiciosAsignablesTrabajador(
    empresaId,
    editingWorker?.id_usuario || null
  );

  const {
    temporadas,
    vigenteHoy,
    saving: savingTemporada,
    crearTemporada,
    editarTemporada,
    borrarTemporada,
  } = useTemporadas(empresaId || '');

  const navigate = useNavigate();
  const [temporadaDialogOpen, setTemporadaDialogOpen] = useState(false);
  const [editandoTemporada, setEditandoTemporada] = useState(false);

  const temporadaActual =
    temporadas.find((t) => t.id_horario_temporada === idTemporada) ?? null;

  const esVigente =
    !!temporadaActual &&
    temporadaActual.id_horario_temporada === vigenteHoy?.id_horario_temporada;


  const {
    franjasDe: franjasTrabajador,
    temporadasConHorario,
    refrescar: refrescarHorariosTrabajador,
    cargado: horariosTrabajadorCargados,
    saving: savingHorarios,
    guardar: guardarHorariosTrabajador,
  } = useHorariosTrabajadorTodos(editingWorker?.id_usuario || null, empresaId || '');

  const {
    franjasDe: franjasEmpresa,
    cargado: horariosEmpresaCargados,
    refrescar: refrescarHorariosEmpresa,
  } = useHorariosEmpresaTodos(empresaId || '');

  const idVigente = vigenteHoy?.id_horario_temporada ?? null;

  const horariosUsuario = franjasTrabajador(idTemporada);
  const horariosGeneralUsuario = franjasTrabajador(null);
  const horariosVigenteUsuario = franjasTrabajador(idVigente);
  const horariosEmpresa = franjasEmpresa(null);
  const horariosEmpresaTemporada = franjasEmpresa(idTemporada);

  const temporadaLeAfecta =
    !!vigenteHoy && (vigenteHoy.cierre || horariosVigenteUsuario.length > 0);

  // Franjas del horario general del trabajador, para enseñarlas de fondo
  // cuando no tiene jornada propia en la temporada seleccionada.
  const generalPorDia = useMemo(() => {
    const map = {} as Record<DiaSemana, { hora_inicio: string; hora_fin: string }[]>;
    DIAS_SEMANA.forEach((d) => (map[d] = []));
    horariosGeneralUsuario.forEach((h: any) => {
      const raw = h.dia_semana.trim().toLowerCase();
      const dia = DIAS_SEMANA.find((d) => d.toLowerCase() === raw);
      if (!dia) return;
      map[dia].push({
        hora_inicio: h.hora_inicio.slice(0, 5),
        hora_fin: h.hora_fin.slice(0, 5),
      });
    });
    return map;
  }, [horariosGeneralUsuario]);

  // Jornada que la empresa ha definido para la temporada seleccionada. Es la
  // que se propone asignar al trabajador.
  const temporadaPorDia = useMemo(() => {
    const map = {} as Record<DiaSemana, { hora_inicio: string; hora_fin: string }[]>;
    DIAS_SEMANA.forEach((d) => (map[d] = []));
    horariosEmpresaTemporada.forEach((h: any) => {
      const raw = h.dia_semana.trim().toLowerCase();
      const dia = DIAS_SEMANA.find((d) => d.toLowerCase() === raw);
      if (!dia) return;
      map[dia].push({
        hora_inicio: h.hora_inicio.slice(0, 5),
        hora_fin: h.hora_fin.slice(0, 5),
      });
    });
    return map;
  }, [horariosEmpresaTemporada]);

  const hayJornadaDeTemporada = horariosEmpresaTemporada.length > 0;

  // Tras quitarle la jornada, lo que se le aplicara es su horario general,
  // asi que es ese el que se enseña de fondo y no la propuesta de la empresa.
  const mostrarPropuestaTemporada = hayJornadaDeTemporada && !jornadaQuitada;

  // Vista de solo lectura de la temporada: primero lo que tenga asignado,
  // luego la jornada de la temporada, y si no hay nada, su horario general.
  const asignadasPorDia = useMemo(() => {
    const map = {} as Record<DiaSemana, { hora_inicio: string; hora_fin: string }[]>;
    DIAS_SEMANA.forEach((d) => (map[d] = []));
    horariosUsuario.forEach((h: any) => {
      const raw = h.dia_semana.trim().toLowerCase();
      const dia = DIAS_SEMANA.find((d) => d.toLowerCase() === raw);
      if (!dia) return;
      map[dia].push({
        hora_inicio: h.hora_inicio.slice(0, 5),
        hora_fin: h.hora_fin.slice(0, 5),
      });
    });
    return map;
  }, [horariosUsuario]);

  const tieneJornadaPropia = horariosUsuario.length > 0 && !jornadaQuitada;

  const franjasTemporadaPorDia = tieneJornadaPropia
    ? asignadasPorDia
    : mostrarPropuestaTemporada
      ? temporadaPorDia
      : generalPorDia;

  const etiquetaFranjas = tieneJornadaPropia
    ? '· asignada'
    : mostrarPropuestaTemporada
      ? '· jornada de la temporada, sin asignar'
      : '· horario general';

  const isInitialLoading =
    loadingServicios ||
    !horariosTrabajadorCargados ||
    !horariosEmpresaCargados;

  const isSaving = loadingInvitar || savingHorarios;

  const isNew = !editingWorker || editingWorker._prefill;

  useEffect(() => {
    if (open) setActiveTab('datos');
  }, [open]);

  useEffect(() => {
    if (!open) {
      setDataLoaded(false);
      horarioCargadoPara.current = false;
      temporadaInicializada.current = false;
      snapshot.current = { form: '', horarios: '', servicios: '' };
      snapshotTomado.current = false;
      setJornadaQuitada(false);
      setTemporadaDialogOpen(false);
      setEditandoTemporada(false);
      accionPendiente.current = null;
      setAvisoOpen(false);
      setIdTemporada(null);
      return;
    }
    if (dataLoaded) return;

    const load = async () => {
      if (editingWorker) {
        setForm({
          email: editingWorker.email,
          fullName: editingWorker.nombre_completo,
          intervaloCita: editingWorker.intervalo_cita || 30,
          activo: editingWorker.activo,
          autocompletarCitas: editingWorker.autocompletar_citas ?? false,
          autocompletarHoras: editingWorker.autocompletar_horas ?? 2,
          autocompletarDesde:
            editingWorker.autocompletar_desde ??
            new Date().toISOString().split('T')[0],
          autocompletarTipoPago:
            editingWorker.autocompletar_tipo_pago ?? 'otros',
        });
      } else {
        setForm({
          email: '',
          fullName: '',
          intervaloCita: 30,
          activo: true,
          autocompletarCitas: false,
          autocompletarHoras: 2,
          autocompletarDesde: new Date().toISOString().split('T')[0],
          autocompletarTipoPago: 'otros',
        });
        const reset = {} as Record<DiaSemana, Schedule[]>;
        DIAS_SEMANA.forEach((d) => (reset[d] = []));
        setSchedulesByDay(reset);
      }
      setDataLoaded(true);
    };

    load();
  }, [open, editingWorker]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (servicios.length === 0) return;
    if (localServices.length === 0) {
      setLocalServices(
        isNew ? servicios.map((s) => ({ ...s, activo: true })) : servicios
      );
    }
  }, [servicios, dataLoaded, isNew]);

  // Foto de referencia, una sola vez y con TODO cargado: servicios, franjas
  // y formulario. Si se toma antes, se registra un estado a medias (cero
  // servicios, cero franjas) y luego la carga real cuenta como cambio.
  useEffect(() => {
    if (!open || !dataLoaded || isInitialLoading) return;
    if (snapshotTomado.current) return;
    // Las franjas ya estan volcadas en el formulario cuando el efecto de
    // carga ha marcado la temporada que acaba de dejar puesta.
    if (horarioCargadoPara.current !== idTemporada) return;
    // Y los servicios, cuando localServices refleja lo que trajo el hook:
    // el efecto que los copia corre en la misma pasada que este, asi que
    // aqui todavia se leeria el array vacio del render anterior.
    if (servicios.length > 0 && localServices.length === 0) return;

    snapshot.current = {
      form: firmaForm(form),
      horarios: firmaHorarios(schedulesByDay),
      servicios: firmaServicios(localServices),
    };
    snapshotTomado.current = true;
  }, [
    open,
    dataLoaded,
    isInitialLoading,
    idTemporada,
    form,
    schedulesByDay,
    localServices,
    servicios,
  ]);

  // Se arranca en la temporada en vigor solo si el trabajador tiene jornada
  // propia en ella. Si no, se abre en el horario general, que es el suyo.
  useEffect(() => {
    if (!open) return;
    refrescarHorariosTrabajador();
    refrescarHorariosEmpresa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (temporadaInicializada.current) return;
    if (temporadas.length === 0) return;
    if (!horariosTrabajadorCargados) return;

    // Solo se abre en una temporada si esta en vigor hoy y el trabajador
    // tiene jornada propia en ella. En cualquier otro caso, horario general.
    const suyas = temporadas.filter((t) =>
      temporadasConHorario.has(t.id_horario_temporada)
    );

    const elegida =
      suyas.find((t) => t.id_horario_temporada === idVigente) ?? null;

    setIdTemporada(elegida?.id_horario_temporada ?? null);
    temporadaInicializada.current = true;
  }, [
    open,
    temporadas,
    idVigente,
    horariosTrabajadorCargados,
    temporadasConHorario,
  ]);

  useEffect(() => {
    setJornadaQuitada(false);
  }, [idTemporada]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (horarioCargadoPara.current === idTemporada) return;
    if (isNew && !horariosEmpresaCargados) return;
    if (!isNew && !horariosTrabajadorCargados) return;

    const grouped = {} as Record<DiaSemana, Schedule[]>;
    DIAS_SEMANA.forEach((d) => (grouped[d] = []));

    if (!isNew) {
      horariosUsuario.forEach((h: any) => {
        const raw = h.dia_semana.trim().toLowerCase();
        const dia = DIAS_SEMANA.find((d) => d.toLowerCase() === raw);
        if (!dia) return;
        grouped[dia].push({
          id_horario: h.id_horario,
          dia_semana: dia,
          hora_inicio: h.hora_inicio.slice(0, 5),
          hora_fin: h.hora_fin.slice(0, 5),
        });
      });
      setSchedulesByDay(grouped);
      snapshot.current.horarios = firmaHorarios(grouped);
      horarioCargadoPara.current = idTemporada;
    } else {
      if (horariosEmpresa && horariosEmpresa.length > 0) {
        horariosEmpresa.forEach((h: any) => {
          const raw = h.dia_semana.trim().toLowerCase();
          const dia = DIAS_SEMANA.find((d) => d.toLowerCase() === raw);
          if (!dia) return;
          grouped[dia].push({
            dia_semana: dia,
            hora_inicio: h.hora_inicio.slice(0, 5),
            hora_fin: h.hora_fin.slice(0, 5),
          });
        });
      }
      setSchedulesByDay(grouped);
      snapshot.current.horarios = firmaHorarios(grouped);
      horarioCargadoPara.current = idTemporada;
    }
  }, [
    horariosUsuario,
    horariosEmpresa,
    dataLoaded,
    isNew,
    idTemporada,
    horariosTrabajadorCargados,
    horariosEmpresaCargados,
  ]);

  const hayCambios =
    dataLoaded &&
    snapshotTomado.current &&
    (firmaForm(form) !== snapshot.current.form ||
      firmaHorarios(schedulesByDay) !== snapshot.current.horarios ||
      firmaServicios(localServices) !== snapshot.current.servicios);


  // Cualquier salida (cerrar, cancelar, cambiar de temporada) pasa por aqui:
  // si hay cambios sin guardar, se pregunta antes de ejecutarla.
  const conConfirmacion = (accion: () => void) => {
    if (!hayCambios) return accion();
    accionPendiente.current = accion;
    setAvisoOpen(true);
  };

  const confirmarSalida = () => {
    setAvisoOpen(false);
    const accion = accionPendiente.current;
    accionPendiente.current = null;
    accion?.();
  };

  const cancelarSalida = () => {
    setAvisoOpen(false);
    accionPendiente.current = null;
  };

  const cerrarDialogo = () => conConfirmacion(() => setOpen(false));

  // Vacia el formulario de la temporada. Al guardar sin franjas, el
  // trabajador se queda sin jornada propia y vuelve al horario general.
  const irAHorariosEmpresa = () =>
    conConfirmacion(() => {
      setOpen(false);
      navigate('/panel-empresa/companyProfile');
    });

  const abrirNuevaTemporada = () => {
    setEditandoTemporada(false);
    setTemporadaDialogOpen(true);
  };

  const abrirEditarTemporada = () => {
    setEditandoTemporada(true);
    setTemporadaDialogOpen(true);
  };

  const handleCrearTemporada = async (form: any) => {
    const res = await crearTemporada(form);
    if (res.ok) {
      setTemporadaDialogOpen(false);
      conConfirmacion(() => setIdTemporada(res.idTemporada));
    }
    return res;
  };

  // Borrar la temporada se lleva por delante las franjas de todos los
  // trabajadores (cascade), asi que hay que volver al horario general.
  const handleBorrarTemporada = async (id: string) => {
    const res = await borrarTemporada(id);
    if (res.ok) {
      setTemporadaDialogOpen(false);
      setIdTemporada(null);
    }
    return res;
  };

  const quitarJornadaTemporada = () => {
    const grouped = {} as Record<DiaSemana, Schedule[]>;
    DIAS_SEMANA.forEach((d) => (grouped[d] = []));
    setSchedulesByDay(grouped);
    setJornadaQuitada(true);
    toast.success(
      'Al guardar volverá a su horario general durante esta temporada.'
    );
  };

  const copiarHorarioEmpresa = async () => {
    const agrupados = await refrescarHorariosEmpresa();
    const horariosActuales = agrupados[idTemporada ?? CLAVE_BASE] ?? [];

    if (horariosActuales.length === 0) {
      toast.error(
        idTemporada
          ? `La empresa no tiene horario para ${temporadaActual?.nombre}`
          : 'La empresa no tiene horarios configurados'
      );
      return;
    }

    const grouped = {} as Record<DiaSemana, Schedule[]>;
    DIAS_SEMANA.forEach((d) => (grouped[d] = []));

    horariosActuales.forEach((h: any) => {
      const raw = h.dia_semana.trim().toLowerCase();
      const dia = DIAS_SEMANA.find((d) => d.toLowerCase() === raw);
      if (!dia) return;
      grouped[dia].push({
        dia_semana: dia,
        hora_inicio: h.hora_inicio.slice(0, 5),
        hora_fin: h.hora_fin.slice(0, 5),
      });
    });

    setSchedulesByDay(grouped);
    toast.success(
      idTemporada
        ? `Horario de ${temporadaActual?.nombre} copiado correctamente.`
        : 'Horario de empresa copiado correctamente.'
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'intervaloCita' ? Number(value) : value,
    }));
  };

  const handleScheduleChange = (
    dia: DiaSemana,
    idx: number,
    field: keyof Schedule,
    value: string
  ) => {
    const newDaySchedules = [...schedulesByDay[dia]];
    newDaySchedules[idx] = { ...newDaySchedules[idx], [field]: value };
    setSchedulesByDay({ ...schedulesByDay, [dia]: newDaySchedules });
  };

  const addScheduleClick = (dia: DiaSemana) => {
    setNewScheduleDay(dia);
    setNewScheduleForm({ dia_semana: dia, hora_inicio: '', hora_fin: '' });
  };

  const handleNewScheduleChange = (field: keyof Schedule, value: string) => {
    setNewScheduleForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveNewSchedule = () => {
    if (!newScheduleDay) return;
    const { hora_inicio, hora_fin } = newScheduleForm;
    if (!hora_inicio || !hora_fin) {
      toast.error('Completa la hora de inicio y fin');
      return;
    }
    setSchedulesByDay({
      ...schedulesByDay,
      [newScheduleDay]: [...schedulesByDay[newScheduleDay], newScheduleForm],
    });
    setNewScheduleDay(null);
    setNewScheduleForm({ dia_semana: 'lunes', hora_inicio: '', hora_fin: '' });
  };

  const cancelNewSchedule = () => {
    setNewScheduleDay(null);
    setNewScheduleForm({ dia_semana: 'lunes', hora_inicio: '', hora_fin: '' });
  };

  const removeSchedule = (dia: DiaSemana, idx: number) => {
    const newArr = [...schedulesByDay[dia]];
    newArr.splice(idx, 1);
    setSchedulesByDay({ ...schedulesByDay, [dia]: newArr });
  };

  const toggleServicioActivo = (id_servicio: string, activo: boolean) => {
    setLocalServices((prev) =>
      prev.map((s) =>
        String(s.id_servicio) === id_servicio ? { ...s, activo } : s
      )
    );
  };

  const handleSubmit = async () => {
    const { email, fullName, intervaloCita, activo } = form;

    if (!email || !fullName) {
      toast.error('Completa todos los campos');
      setActiveTab('datos');
      return;
    }

    if (!empresaId || !idUsuario) {
      toast.error('No se encontró empresa/usuario');
      return;
    }

    const formChanged = editingWorker
      ? fullName !== editingWorker.nombre_completo ||
        email !== editingWorker.email ||
        intervaloCita !== editingWorker.intervalo_cita ||
        activo !== editingWorker.activo ||
        form.autocompletarCitas !==
          (editingWorker.autocompletar_citas ?? false) ||
        form.autocompletarHoras !== (editingWorker.autocompletar_horas ?? 2) ||
        form.autocompletarDesde !==
          (editingWorker.autocompletar_desde ?? null) ||
        form.autocompletarTipoPago !==
          (editingWorker.autocompletar_tipo_pago ?? 'otros')
      : true;

    const serviciosChanged = editingWorker
      ? JSON.stringify(servicios.map((s) => s.activo)) !==
        JSON.stringify(localServices.map((s) => s.activo))
      : true;

    const horariosChanged = editingWorker
      ? JSON.stringify(schedulesByDay) !==
        JSON.stringify(
          (() => {
            const grouped = {} as Record<DiaSemana, Schedule[]>;
            DIAS_SEMANA.forEach((d) => (grouped[d] = []));
            horariosUsuario.forEach((h: any) => {
              const raw = h.dia_semana.trim().toLowerCase();
              const dia = DIAS_SEMANA.find((d) => d.toLowerCase() === raw);
              if (dia) {
                grouped[dia].push({
                  id_horario: h.id_horario,
                  dia_semana: dia,
                  hora_inicio: h.hora_inicio,
                  hora_fin: h.hora_fin,
                });
              }
            });
            return grouped;
          })()
        )
      : true;

    if (!formChanged && !serviciosChanged && !horariosChanged) {
      toast.info('No hay cambios para guardar');
      return;
    }

    setSaving(true);
    try {
      let createdUserId: string | null = null;

      if (isNew) {
        const { userId: newUserId, error } = await invitar(
          email,
          fullName,
          intervaloCita,
          activo,
          localServices
        );
        if (error) return toast.error(error);
        if (!newUserId) {
          toast.error('Error creando trabajador');
          return;
        }
        createdUserId = newUserId;
      }

      if (!isNew) {
        const result = await editarTrabajador(
          editingWorker.id_usuario,
          fullName,
          intervaloCita,
          activo,
          empresaId,
          idUsuario,
          editingWorker.id_perfil,
          form.autocompletarCitas,
          form.autocompletarHoras,
          form.autocompletarDesde,
          form.autocompletarTipoPago
        );
        if (!result.ok) return toast.error(result.error);
      }

      if (!isNew && serviciosChanged) {
        const r = await guardarServiciosTrabajador(
          editingWorker.id_usuario,
          localServices as ServicioAsignable[]
        );
        if (!r.ok) return toast.error(r.error);
      }

      const trabajadorId = !isNew ? editingWorker.id_usuario : createdUserId;

      if (trabajadorId) {
        const r = await guardarHorariosTrabajador(
          schedulesByDay,
          idUsuario,
          trabajadorId,
          idTemporada
        );

        if (!r.ok) return toast.error('Error guardando horarios');
      }

      if (!isNew) {
        toast.success('Trabajador actualizado');
        setOpen(false);
        setTimeout(onSaved, 10);
      } else if (editingWorker?._prefill) {
        toast.success('¡Te has registrado como trabajador correctamente!');
        setOpen(false);
        setTimeout(onSaved, 10);
      } else {
        setShowInviteInfo(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const totalHorarios = DIAS_SEMANA.reduce(
    (acc, d) => acc + schedulesByDay[d].length,
    0
  );
  const serviciosActivos = localServices.filter((s) => s.activo).length;

  const tabs: {
    id: Tab;
    label: string;
    icon: React.ReactNode;
    badge?: number;
  }[] = [
    { id: 'datos', label: 'Datos', icon: <User className="w-4 h-4" /> },
    {
      id: 'horarios',
      label: 'Horarios',
      icon: <Clock className="w-4 h-4" />,
      badge: totalHorarios || undefined,
    },
    {
      id: 'servicios',
      label: 'Servicios',
      icon: <Briefcase className="w-4 h-4" />,
      badge: serviciosActivos || undefined,
    },
  ];

  return (
    <>
      {/* ── DIALOG PRINCIPAL ── */}
      {/* El aviso lo oculta, no lo cierra: `open` sigue a true, asi que el
          formulario y los cambios se conservan si se sigue editando. */}
      <Dialog
        open={
          open &&
          !showInviteInfo &&
          !avisoOpen &&
          !temporadaDialogOpen
        }
        onOpenChange={(abierto) => {
          if (abierto) return setOpen(true);
          if (avisoOpen || temporadaDialogOpen) return;
          cerrarDialogo();
        }}
      >
        <DialogContent className={SHEET_CLASS}>
          {!dataLoaded || isInitialLoading ? (
            <div className="h-80 flex flex-col justify-center items-center gap-4 p-6">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#103254] dark:border-blue-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Cargando datos...
              </p>
            </div>
          ) : (
            <>
              {/* ── HEADER ── */}
              <div className="px-4 sm:px-6 pt-5 pb-0 shrink-0 relative">
                <DialogHeader>
                  <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
                    {!isNew ? 'Editar trabajador' : 'Nuevo trabajador'}
                  </DialogTitle>
                </DialogHeader>
                <button
                  onClick={cerrarDialogo}
                  className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* ── TABS ── */}
              <div className="px-3 pt-4 shrink-0">
                <div className="flex gap-0.5 bg-gray-100 dark:bg-white/5 rounded-lg p-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-2 rounded-md transition-all min-w-0 ${
                        activeTab === tab.id
                          ? 'bg-white dark:bg-white/10 text-[#103254] dark:text-blue-400 shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      <span className="shrink-0">{tab.icon}</span>
                      <span className="text-[11px] font-medium truncate hidden sm:block">
                        {tab.label}
                      </span>
                      {tab.badge !== undefined && (
                        <span
                          className={`text-[9px] rounded-full px-1 py-0.5 font-bold leading-none shrink-0 ${
                            activeTab === tab.id
                              ? 'bg-[#103254] text-white dark:bg-blue-500'
                              : 'bg-gray-300 dark:bg-white/10 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── CONTENIDO ── */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                {/* TAB: DATOS */}
                {activeTab === 'datos' && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                        Nombre completo
                      </label>
                      <Input
                        name="fullName"
                        value={form.fullName}
                        onChange={handleChange}
                        placeholder="Ej. Ana García"
                        className="rounded-lg dark:bg-white/5 dark:border-white/10 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                        Email
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        disabled={!!editingWorker && !editingWorker._prefill}
                        placeholder="correo@ejemplo.com"
                        className="rounded-lg dark:bg-white/5 dark:border-white/10 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                        Intervalo de cita (min)
                      </label>
                      <Input
                        name="intervaloCita"
                        type="number"
                        min={5}
                        step={5}
                        value={form.intervaloCita}
                        onChange={handleChange}
                        className="rounded-lg dark:bg-white/5 dark:border-white/10 dark:text-white w-32"
                      />
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Perfil activo
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          El trabajador podrá recibir citas
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={form.activo}
                          onCheckedChange={(checked) =>
                            setForm((p) => ({ ...p, activo: checked }))
                          }
                        />
                        <span
                          className={`text-sm font-medium ${
                            form.activo
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-500 dark:text-red-400'
                          }`}
                        >
                          {form.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Autocompletar citas
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Completa automáticamente las citas pasadas
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={form.autocompletarCitas}
                          onCheckedChange={(checked) =>
                            setForm((p) => ({
                              ...p,
                              autocompletarCitas: checked,
                              autocompletarDesde: checked
                                ? p.autocompletarDesde ??
                                  new Date().toISOString().split('T')[0]
                                : p.autocompletarDesde,
                            }))
                          }
                        />
                        <span
                          className={`text-sm font-medium ${
                            form.autocompletarCitas
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-500 dark:text-red-400'
                          }`}
                        >
                          {form.autocompletarCitas ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>

                    {form.autocompletarCitas && (
                      <div className="space-y-3 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Horas tras fin de cita para completar
                          </label>
                          <Input
                            type="number"
                            min={1}
                            max={72}
                            value={form.autocompletarHoras}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                autocompletarHoras: Number(e.target.value),
                              }))
                            }
                            className="rounded-lg dark:bg-white/5 dark:border-white/10 dark:text-white w-24"
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Tipo de pago por defecto
                          </label>
                          <Select
                            value={form.autocompletarTipoPago}
                            onValueChange={(v) =>
                              setForm((p) => ({
                                ...p,
                                autocompletarTipoPago: v,
                              }))
                            }
                          >
                            <SelectTrigger className="focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="z-[70]">
                              <SelectItem value="tarjeta">Tarjeta</SelectItem>
                              <SelectItem value="efectivo">Efectivo</SelectItem>
                              <SelectItem value="bizum">Bizum</SelectItem>
                              <SelectItem value="bono">Bono</SelectItem>
                              <SelectItem value="otros">Otros</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Completar citas desde
                          </label>
                          <Input
                            type="date"
                            value={form.autocompletarDesde ?? ''}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                autocompletarDesde: e.target.value || null,
                              }))
                            }
                            className="rounded-lg dark:bg-white/5 dark:border-white/10 dark:text-white w-48 min-w-0 appearance-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* TAB: HORARIOS */}
                {activeTab === 'horarios' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {(
                        <Select
                          value={idTemporada ?? 'base'}
                          onValueChange={(v) => {
                            if (v === 'nueva') {
                              abrirNuevaTemporada();
                              return;
                            }
                            conConfirmacion(() =>
                              setIdTemporada(v === 'base' ? null : v)
                            );
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs font-semibold flex-1 focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-gray-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[70]">
                            <SelectItem value="base" className="text-sm">
                              Horario general
                            </SelectItem>
                            {temporadas.map((t) => (
                              <SelectItem
                                key={t.id_horario_temporada}
                                value={t.id_horario_temporada}
                                className="text-sm"
                              >
                                {t.nombre}
                                {temporadasConHorario.has(
                                  t.id_horario_temporada
                                )
                                  ? ' · jornada propia'
                                  : ''}
                                {t.id_horario_temporada ===
                                vigenteHoy?.id_horario_temporada
                                  ? ' · en vigor'
                                  : ''}
                              </SelectItem>
                            ))}
                            <SelectItem
                              value="nueva"
                              className="text-sm font-semibold"
                            >
                              + Nueva temporada
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}

                      {temporadaActual && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={abrirEditarTemporada}
                          className="h-8 px-2.5 rounded-lg text-xs font-medium text-[#103254] dark:text-blue-400 border-[#103254] dark:border-blue-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors shrink-0 ml-auto flex items-center gap-1.5"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Editar
                        </Button>
                      )}

                      {!temporadaActual && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copiarHorarioEmpresa}
                          className="rounded-lg text-xs font-medium text-[#103254] dark:text-blue-400 border-[#103254] dark:border-blue-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors ml-auto shrink-0"
                        >
                          Copiar horario de empresa
                        </Button>
                      )}
                    </div>

                    {temporadaActual?.cierre ? (
                      <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                          {esVigente
                            ? 'Ahora mismo no se le puede reservar'
                            : 'No se le podrá reservar'}
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                          {temporadaActual.nombre} está marcada como cerrada
                          (del {formatearFechaCorta(temporadaActual.fecha_desde)}{' '}
                          al {formatearFechaCorta(temporadaActual.fecha_hasta)}).
                          No hay horario que configurar.
                        </p>
                      </div>
                    ) : temporadaActual && !hayJornadaDeTemporada ? (
                      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3">
                        <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                          Esta temporada no tiene horario
                        </p>
                        <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                          Nadie ha definido las horas de {temporadaActual.nombre}{' '}
                          (del {formatearFechaCorta(temporadaActual.fecha_desde)}{' '}
                          al {formatearFechaCorta(temporadaActual.fecha_hasta)}) en
                          el horario de la empresa. Mientras siga así, la
                          temporada no cambia nada: cada trabajador mantiene su
                          horario general, que es el que ves abajo.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={irAHorariosEmpresa}
                          className="mt-2 h-8 px-2.5 rounded-lg text-xs font-medium text-red-800 dark:text-red-300 border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors flex items-center gap-1.5"
                        >
                          <Clock className="w-3.5 h-3.5" />
                          Definir el horario de la temporada
                        </Button>
                      </div>
                    ) : temporadaActual && totalHorarios === 0 ? (
                      <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
                        <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                          {esVigente
                            ? 'Ahora mismo trabaja con su horario general'
                            : 'Seguirá con su horario general'}
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                          {jornadaQuitada
                            ? `Al guardar volverá a su horario general durante ${temporadaActual.nombre}. Abajo tienes las horas que hará.`
                            : hayJornadaDeTemporada
                              ? `Abajo tienes la jornada de ${temporadaActual.nombre} (del ${formatearFechaCorta(temporadaActual.fecha_desde)} al ${formatearFechaCorta(temporadaActual.fecha_hasta)}). Todavía no es suya: asígnasela para que la haga.`
                              : `Nadie ha definido la jornada de ${temporadaActual.nombre} en el horario de la empresa, así que este trabajador mantiene el de siempre.`}
                        </p>

                        <div className="flex flex-wrap gap-2 mt-2">
                          {hayJornadaDeTemporada && (
                            <Button
                              size="sm"
                              onClick={copiarHorarioEmpresa}
                              className="h-8 px-3 rounded-lg text-xs font-semibold bg-[#103254] hover:bg-[#0d2842] text-white"
                            >
                              Asignarle esta jornada
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : temporadaActual ? (
                      <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3">
                        <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                          {esVigente
                            ? 'Este es su horario actual'
                            : 'Tiene una jornada propia para esta temporada'}
                        </p>
                        <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
                          {esVigente
                            ? `En vigor por ${temporadaActual.nombre} hasta el ${formatearFechaCorta(temporadaActual.fecha_hasta)}. Después vuelve a su horario general.`
                            : `Del ${formatearFechaCorta(temporadaActual.fecha_desde)} al ${formatearFechaCorta(temporadaActual.fecha_hasta)} hará esta jornada. Fuera de esas fechas, su horario general.`}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={quitarJornadaTemporada}
                          className="mt-2 h-8 px-2.5 rounded-lg text-xs font-medium text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                        >
                          Quitarle esta jornada
                        </Button>
                      </div>
                    ) : !temporadaActual && temporadaLeAfecta && vigenteHoy ? (
                      <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          Ahora mismo está en vigor{' '}
                          <span className="font-semibold">
                            {vigenteHoy.nombre}
                          </span>
                          , hasta el{' '}
                          {formatearFechaCorta(vigenteHoy.fecha_hasta)}. Este
                          horario general se aplicará a partir de esa fecha.
                        </p>
                      </div>
                    ) : null}

                    {!temporadaActual && temporadasConHorario.size > 0 && (
                      <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3">
                        <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                          Tiene jornada propia en otras fechas
                        </p>
                        <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
                          Este es su horario general, el que trabaja hoy. Aparte
                          tiene jornada propia en{' '}
                          {temporadas
                            .filter((t) =>
                              temporadasConHorario.has(t.id_horario_temporada)
                            )
                            .map(
                              (t) =>
                                `${t.nombre} (${formatearFechaCorta(
                                  t.fecha_desde
                                )} - ${formatearFechaCorta(t.fecha_hasta)})`
                            )
                            .join(', ')}
                          .
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {temporadas
                            .filter((t) =>
                              temporadasConHorario.has(t.id_horario_temporada)
                            )
                            .map((t) => (
                              <Button
                                key={t.id_horario_temporada}
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  conConfirmacion(() =>
                                    setIdTemporada(t.id_horario_temporada)
                                  )
                                }
                                className="h-8 px-2.5 rounded-lg text-xs font-medium text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                              >
                                Ver {t.nombre}
                              </Button>
                            ))}
                        </div>
                      </div>
                    )}

                    {temporadaActual && hayJornadaDeTemporada && (
                      <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-4 py-3">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          El horario de una temporada es el mismo para toda la
                          empresa, así que aquí solo se consulta. Para cambiar
                          las horas de {temporadaActual.nombre}, ve a Horarios
                          en el perfil de la empresa.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={irAHorariosEmpresa}
                          className="mt-2 h-8 px-2.5 rounded-lg text-xs font-medium text-[#103254] dark:text-blue-400 border-[#103254] dark:border-blue-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors flex items-center gap-1.5"
                        >
                          <Clock className="w-3.5 h-3.5" />
                          Ir a Horarios de la empresa
                        </Button>
                      </div>
                    )}

                    {temporadaActual ? (
                      <div className="space-y-2">
                        {DIAS_SEMANA.map((dia) => (
                          <div
                            key={dia}
                            className="border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 bg-gray-50 dark:bg-white/5 flex items-baseline justify-between gap-3"
                          >
                            <h4 className="font-semibold text-sm text-[#103254] dark:text-blue-400 capitalize shrink-0">
                              {dia}
                            </h4>
                            <div className="text-right">
                              {franjasTemporadaPorDia[dia].length > 0 ? (
                                franjasTemporadaPorDia[dia].map((f, i) => (
                                  <p
                                    key={i}
                                    className="text-xs text-gray-600 dark:text-gray-400"
                                  >
                                    {f.hora_inicio} - {f.hora_fin}
                                    {i === 0 && (
                                      <span className="text-gray-400 dark:text-gray-500 italic">
                                        {' '}
                                        {etiquetaFranjas}
                                      </span>
                                    )}
                                  </p>
                                ))
                              ) : (
                                <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                  No trabaja este día
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                    {DIAS_SEMANA.map((dia) => (
                      <div
                        key={dia}
                        className="border border-gray-200 dark:border-white/10 rounded-lg p-3 bg-gray-50 dark:bg-white/5"
                      >
                        <div className="flex justify-between items-center mb-2 gap-2">
                          <h4 className="font-semibold text-sm text-[#103254] dark:text-blue-400 capitalize shrink-0">
                            {dia}
                          </h4>

                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addScheduleClick(dia)}
                              className="h-8 px-2.5 rounded-lg text-xs font-medium text-[#103254] dark:text-blue-400 border-[#103254] dark:border-blue-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                            >
                              + Añadir
                            </Button>

                            <Select
                              value=""
                              onValueChange={(sourceDay) => {
                                const dia_ = dia;
                                if (
                                  sourceDay &&
                                  schedulesByDay[sourceDay as DiaSemana]
                                    ?.length > 0
                                ) {
                                  const copied = schedulesByDay[
                                    sourceDay as DiaSemana
                                  ].map((sch) => ({
                                    ...sch,
                                    dia_semana: dia_,
                                  }));
                                  setSchedulesByDay((prev) => ({
                                    ...prev,
                                    [dia_]: copied,
                                  }));
                                  toast.success(
                                    `Horarios copiados de ${sourceDay} a ${dia_}`
                                  );
                                }
                              }}
                            >
                              <SelectTrigger className="h-8 text-[11px] font-semibold min-w-[100px] focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-gray-200">
                                <SelectValue placeholder="Copiar de…" />
                              </SelectTrigger>
                              <SelectContent className="z-[70]">
                                {DIAS_SEMANA.filter(
                                  (d) =>
                                    d !== dia && schedulesByDay[d].length > 0
                                ).map((d) => (
                                  <SelectItem
                                    key={d}
                                    value={d}
                                    className="text-sm capitalize"
                                  >
                                    {d}
                                  </SelectItem>
                                ))}
                                {DIAS_SEMANA.filter(
                                  (d) =>
                                    d !== dia && schedulesByDay[d].length > 0
                                ).length === 0 && (
                                  <div className="px-3 py-2 text-xs text-gray-400 italic">
                                    Sin días con horario
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {schedulesByDay[dia].map((schedule, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-2 py-2 shadow-sm"
                            >
                              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                  <span className="text-[10px] text-gray-400 font-medium w-5 shrink-0">
                                    De
                                  </span>
                                  <Input
                                    type="time"
                                    value={schedule.hora_inicio}
                                    onChange={(e) =>
                                      handleScheduleChange(
                                        dia,
                                        idx,
                                        'hora_inicio',
                                        e.target.value
                                      )
                                    }
                                    className="h-9 text-sm rounded-md flex-1 min-w-0 appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                                  />
                                </div>
                                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                  <span className="text-[10px] text-gray-400 font-medium w-5 shrink-0">
                                    A
                                  </span>
                                  <Input
                                    type="time"
                                    value={schedule.hora_fin}
                                    onChange={(e) =>
                                      handleScheduleChange(
                                        dia,
                                        idx,
                                        'hora_fin',
                                        e.target.value
                                      )
                                    }
                                    className="h-9 text-sm rounded-md flex-1 min-w-0 appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                                  />
                                </div>
                              </div>

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeSchedule(dia, idx)}
                                className="text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 h-9 w-9 shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}

                          {newScheduleDay === dia && (
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                <span className="text-[10px] text-gray-400 font-medium w-5 shrink-0">
                                  De
                                </span>
                                <Input
                                  type="time"
                                  value={newScheduleForm.hora_inicio}
                                  onChange={(e) =>
                                    handleNewScheduleChange(
                                      'hora_inicio',
                                      e.target.value
                                    )
                                  }
                                  className="h-9 text-sm rounded-md flex-1 min-w-0 appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                                />
                              </div>
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                <span className="text-[10px] text-gray-400 font-medium w-5 shrink-0">
                                  A
                                </span>
                                <Input
                                  type="time"
                                  value={newScheduleForm.hora_fin}
                                  onChange={(e) =>
                                    handleNewScheduleChange(
                                      'hora_fin',
                                      e.target.value
                                    )
                                  }
                                  className="h-9 text-sm rounded-md flex-1 min-w-0 appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={saveNewSchedule}
                                  className="h-9 px-3 rounded-lg text-xs font-medium text-white bg-[#103254] hover:bg-[#0d2742] transition-colors flex-1 sm:flex-none"
                                >
                                  Guardar
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={cancelNewSchedule}
                                  className="h-9 w-9 p-0 rounded-lg dark:border-white/10 flex items-center justify-center shrink-0"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          )}

                          {schedulesByDay[dia].length === 0 &&
                            newScheduleDay !== dia && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 py-1 italic">
                                Sin horarios
                              </p>
                            )}
                        </div>
                      </div>
                    ))}
                      </>
                    )}
                  </div>
                )}

                {/* TAB: SERVICIOS */}
                {activeTab === 'servicios' && (
                  <div className="space-y-2">
                    {localServices.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                        No hay servicios disponibles.
                      </p>
                    ) : (
                      localServices.map((serv) => {
                        const id = String(serv.id_servicio);
                        return (
                          <div
                            key={id}
                            className={`flex justify-between items-center p-3 rounded-lg border shadow-sm transition-opacity ${
                              serv.activo
                                ? 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10'
                                : 'bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/10 opacity-60'
                            }`}
                          >
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {serv.servicio}
                            </span>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={serv.activo}
                                onCheckedChange={(checked) =>
                                  toggleServicioActivo(id, checked)
                                }
                              />
                              <span
                                className={`text-xs font-medium w-12 text-right ${
                                  serv.activo
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-red-500 dark:text-red-400'
                                }`}
                              >
                                {serv.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* ── FOOTER ── */}
              <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
                <Button
                  variant="outline"
                  onClick={cerrarDialogo}
                  disabled={isInitialLoading || isSaving}
                  className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isInitialLoading || isSaving}
                  className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
                >
                  {isSaving
                    ? !isNew
                      ? 'Guardando...'
                      : 'Invitando...'
                    : !isNew
                    ? 'Guardar cambios'
                    : 'Invitar'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── DIALOG INVITACIÓN ENVIADA ── */}
      <Dialog
        open={showInviteInfo}
        onOpenChange={() => {
          setShowInviteInfo(false);
          setOpen(false);
          setTimeout(onSaved, 10);
        }}
      >
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-sm sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-6 pb-3">
            <DialogHeader>
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#103254]/10 dark:bg-blue-400/10">
                  <MailCheck className="w-7 h-7 text-[#103254] dark:text-blue-400" />
                </div>
                <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400">
                  ¡Invitación enviada!
                </DialogTitle>
              </div>
            </DialogHeader>
          </div>

          <div className="px-4 sm:px-6 pb-3 text-sm text-gray-700 dark:text-gray-300 space-y-3 text-center">
            <p>
              El trabajador{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {form.fullName}
              </span>{' '}
              ha sido invitado correctamente. Le hemos enviado un correo de
              invitación a{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {form.email}
              </span>
              .
            </p>
            <p>
              Para acceder a la plataforma, debe revisar su bandeja de entrada y
              aceptar la invitación haciendo clic en el enlace del correo.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-white/5 rounded-lg px-3 py-2 border border-gray-200 dark:border-white/10">
              ¿No lo ves? Pídele que revise la carpeta de{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                spam
              </span>{' '}
              o{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                correo no deseado
              </span>
              .
            </p>
          </div>

          <DialogFooter className="px-4 sm:px-6 pb-5 sm:pb-4 pt-2 border-t border-gray-100 dark:border-white/10">
            <Button
              onClick={() => {
                setShowInviteInfo(false);
                setOpen(false);
                setTimeout(onSaved, 10);
              }}
              className={`w-full ${DLG_BTN_PRIMARY}`}
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemporadaDialog
        open={temporadaDialogOpen}
        onClose={() => setTemporadaDialogOpen(false)}
        temporada={editandoTemporada ? temporadaActual : null}
        saving={savingTemporada}
        onCrear={handleCrearTemporada}
        onEditar={editarTemporada}
        onBorrar={handleBorrarTemporada}
      />

      <UnsavedChangesDialog
        open={avisoOpen}
        onConfirm={confirmarSalida}
        onCancel={cancelarSalida}
        description="Si sales ahora, perderás los cambios del trabajador."
      />
    </>
  );
};